import React, { useMemo } from 'react';
import ExpandableFilterPanel, {
  ExpandableFilterPanelItem,
} from 'components/molecules/expandable-filter-panel';
import { useSmsCampaignQuestionsSelect } from 'infrastructure/hooks/select/use-sms-campaigns-question';
import BaseLoader from 'components/atoms/base-loader';
import BaseSelectBox from 'components/atoms/base-select-box';

import s from './styles.module.scss';
import StatusSelect from './status-select';
import LanguageSelect from './language-select';

import type { TResetFilterFn } from 'infrastructure/hooks/common/use-query-param-new';
import type { ISmsResponsesFilters } from 'infrastructure/interfaces';

type Filter = Record<string, string[]>;

interface IProps {
  filters: ISmsResponsesFilters;
  updateFilter: (data: ISmsResponsesFilters) => void;
  resetFilter: TResetFilterFn<ISmsResponsesFilters>;
  count: number;
  campaignGuid: string;
}

const SmsCampaignsResponsesTableFilters: React.FC<IProps> = (props) => {
  const { filters, updateFilter, resetFilter, count, campaignGuid } = props;

  const parsedFilter = useMemo<Filter>(() => {
    if (filters.questionAnswers)
      return JSON.parse(filters.questionAnswers) as Filter;
    return {};
  }, [filters.questionAnswers]);

  const { data, isLoading } = useSmsCampaignQuestionsSelect({
    campaignGuid,
    groupName: filters.groupName,
    language: filters.language,
    excludeInfoType: true,
  });

  const handleUpdateFilter = (
    values: Filter,
    key: string,
    value: string | string[],
    overwrite: boolean = false,
  ) => {
    const filter = structuredClone(values);
    const newValues = Array.isArray(value) ? value : [value];

    if (!value || newValues.length === 0) {
      delete filter[key];
    } else {
      const existing = filter[key];
      if (
        overwrite ||
        !(key in filter) ||
        !Array.isArray(existing) ||
        newValues.length < (Array.isArray(existing) ? existing.length : 0)
      ) {
        filter[key] = newValues;
      } else {
        filter[key] = Array.from(
          new Set([...(existing as string[]), ...newValues]),
        );
      }
    }

    updateFilter({
      questionAnswers: JSON.stringify(filter),
    });
  };

  const isMultiple = (type: string) => type === 'multiple';

  return (
    <ExpandableFilterPanel
      filtersCount={count}
      onReset={() => {
        resetFilter();
      }}
      openWithValue
      additionalControls={
        <div className={s.additionalControls}>
          <StatusSelect
            value={filters.groupName}
            onChange={(groupName) => {
              updateFilter({ groupName, questionAnswers: undefined });
            }}
          />
          <LanguageSelect
            value={filters.language}
            onChange={(language) => {
              updateFilter({ language, questionAnswers: undefined });
            }}
          />
        </div>
      }
    >
      <BaseLoader loading={isLoading}>
        <div className={s.form}>
          {data.map((q) => (
            <ExpandableFilterPanelItem
              label={q.question}
              key={q.guid}
              borderBottom
            >
              <BaseSelectBox
                value={parsedFilter[q.guid]}
                mode={isMultiple(q.type) ? 'multiple' : undefined}
                allowClear
                allowSearch
                options={q.answers.map((a) => ({
                  label: a.answer,
                  value: a.guid,
                }))}
                onChange={(val) =>
                  handleUpdateFilter(
                    parsedFilter,
                    q.guid,
                    val,
                    !isMultiple(q.type),
                  )
                }
              />
            </ExpandableFilterPanelItem>
          ))}
        </div>
      </BaseLoader>
    </ExpandableFilterPanel>
  );
};

export default SmsCampaignsResponsesTableFilters;
